import React, { useEffect, useRef, useState } from 'react';
import jwt_decode from 'jwt-decode';
import * as _ from 'lodash';

import { IonContent, RefresherEventDetail } from '@ionic/react';

import { Factory } from '../factory';
import { useSelector } from 'react-redux';
import { getMetaResolver, getResolver } from './resolvers.registry';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, setNavigate, Inspector } from '@flexo/atoms';
import { getSiteLayoutHelper } from './helpers';
import { auth, IconWrapper, useSafeArea } from '@flexo/general';
import { useDispatch } from 'react-redux';
import { ALERT, THEME } from '@flexo/reducers';
import { Keyboard } from '@capacitor/keyboard';

const ENV = (import.meta as any).env;

export const Modules = (props: any) => {
  const navigate = useNavigate();
  setNavigate(navigate);
  const params = useParams();
  const alertStore = useSelector((state: any) => state.alert);

  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);
  const theme = useSelector((state: any) => state.theme);

  const location = useLocation();
  const dispatch = useDispatch();
  const pathSegment = location.pathname.split('/')[1];

  const [activeModule, setActiveModule] = useState<any | null>(null);
  const [layouts, setLayouts] = useState<any | null>(null);
  const [widgets, setWidgets] = useState<any | null>(null);

  const [shownInspector, setShownInspector] = useState<any>(null);
  const [devModeActive, setDevModeActive] = useState<any>(false);
  const [showSplashScreen, setShowSplashScreen] = useState<any>(true);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const scrollRef = useRef<HTMLIonContentElement>(null);
  const equalizerRef = useRef<HTMLDivElement>(null);

  const { isAndroid, isIPhone16OrDynamicIslandDevice } = useSafeArea();

  function scrollToTop() {
    if (ENV?.VITE_APP_IS_WEB === 'true') {
      scrollRef.current?.scrollToTop(500);
    }
  }

  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  // Equalize InformationCard heights per row (web only)
  useEffect(() => {
    if (ENV?.VITE_APP_IS_WEB !== 'true') return;
    if (typeof window === 'undefined') return;
    const container = equalizerRef.current as HTMLElement | null;
    if (!container) return;

    const selector = '[class*="resizeable"]';
    let scheduled: number | null = null;
    let isEqualizing = false;
    let imageListeners: Array<{ img: HTMLImageElement; handler: () => void }> = [];

    const equalize = () => {
      if (isEqualizing) return;
      isEqualizing = true;
      const items = Array.from(container.querySelectorAll<HTMLElement>(selector));
      if (!items.length) { isEqualizing = false; return; }
      // reset before measure
      items.forEach(itm => { itm.style.height = 'auto'; itm.style.minHeight = ''; itm.style.maxHeight = ''; });
      // group by row via offsetTop
      const rows = new Map<number, HTMLElement[]>();
      for (const itm of items) {
        const top = Math.round(itm.offsetTop);
        const group = rows.get(top) || [];
        group.push(itm);
        rows.set(top, group);
      }
      // set max height per row
      rows.forEach(group => {
        const maxH = Math.max(...group.map(g => g.clientHeight));
        group.forEach(g => {
          const target = `${maxH}px`;
          if (g.style.height !== target || g.style.minHeight !== target || g.style.maxHeight !== target) {
            g.style.height = target;
            g.style.minHeight = target;
            g.style.maxHeight = target;
          }
        });
      });
      isEqualizing = false;
    };

    const schedule = () => {
      if (scheduled != null) return;
      scheduled = window.requestAnimationFrame(() => {
        scheduled = null;
        equalize();
      });
    };

    // Observe size and DOM changes inside the container
    const ro = new ResizeObserver(() => schedule());
    ro.observe(container);
    const mo = new MutationObserver(() => schedule());
    mo.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });

    // Re-equalize when images finish loading
    const imgs = Array.from(container.querySelectorAll<HTMLImageElement>('img'));
    imgs.forEach(img => {
      if (!img.complete) {
        const handler = () => schedule();
        imageListeners.push({ img, handler });
        img.addEventListener('load', handler);
        img.addEventListener('error', handler);
      }
    });

    // On window load and when fonts are ready
    window.addEventListener('load', schedule);
    if ((document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(() => schedule());
    }

    // initial run
    schedule();
    window.addEventListener('resize', schedule);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
      if (scheduled != null) {
        cancelAnimationFrame(scheduled);
        scheduled = null;
      }
      imageListeners.forEach(({ img, handler }) => {
        img.removeEventListener('load', handler);
        img.removeEventListener('error', handler);
      });
      imageListeners = [];
    };
  }, [ location.pathname, widgets ]);

  function getFilteredWidgets(activeModule) {
    let authContent: any = null;

    try {
      const accessToken =
        (auth as any)?.currentUser?.accessToken ||
        (auth as any)?.currentUser?.stsTokenManager?.accessToken ||
        user?.tokenContent?.accessToken ||
        user?._tokenResponse?.idToken;

      if (accessToken) {
        authContent = jwt_decode(accessToken);
      }
    } catch (err: any) {
      console.warn('Token decode failed in widget filtering:', err.message);
    }

    const siteType: any =
      getSiteLayoutHelper(
        metadata,
        pathSegment,
        params,
        activeModule,
        props.MODULE
      ) || 'consumer';
    const role =
      authContent?.user_role || user?.tokenContent?.user_role || 'default';

    const defaultWidgets = _.keyBy(activeModule?.widgets?.default, 'id');
    const activeWidgets: any = _.keyBy(
      activeModule?.widgets?.[role]?.[siteType] || {},
      'id'
    );

    // Merge default and active widgets by id: update default properties with active ones
    const allIds = Array.from(
      new Set([...Object.keys(defaultWidgets), ...Object.keys(activeWidgets)])
    );
    const mergedWidgetsById: Record<string, any> = {};
    allIds.forEach((id) => {
      mergedWidgetsById[id] = {
        ...defaultWidgets[id],
        ...activeWidgets[id],
      };
    });
    const _widgets = Object.values(mergedWidgetsById)
      .filter((widget: any) => widget?.keep !== false)
      .sort((a: any, b: any) => a.order - b.order);

    return _widgets;
  }

  function handleRefresh(event: CustomEvent<RefresherEventDetail>) {
    setTimeout(() => {
      // TODO: Z - There is basically almost never a good reason to have a timeout, it spawns a new thread and is not reliable

      // This is just to test the pull to refresh functionality, if it closes after 2 seconds or not.
      // Not the actual code XD

      // Any calls to load data go here

      //create function that check metadataapi store and calendar sotre and calls http service

      event.detail.complete();
    }, 2000);
  }

  function showInspector(widget: any) {
    if (ENV.VITE_APP_ALLOW_INSPECTION !== 'true') {
      return;
    }
    if (devModeActive === false) {
      return;
    }

    const _widget: any = {
      ...widget,
    };

    delete _widget.layouts;
    delete _widget.setActiveModule;
    delete _widget.showInspector;

    setShownInspector(_widget);
  }

  const Resolvers = React.useMemo(() => {
    if (activeModule === null) return null;

    return activeModule?.apis?.default?.map((resolver: any) => {
      const Resolver = getResolver(resolver);
      return Resolver;
    });
  }, [activeModule]);

  const MetaResolvers = React.useMemo(() => {
    if (activeModule === null) return null;
    return activeModule?.metaResolvers?.default?.map((resolver: any) => {
      const MetaResolver = getMetaResolver(resolver);
      return MetaResolver;
    });
  }, [activeModule]);

  // setting colors
  useEffect(() => {
    if (Object.keys(theme?.colors)?.length > 0) return;
    dispatch(THEME.SetColors({ colors: props.COLORS } as any));
  }, []);

  useEffect(() => {
    // console.log('props.MODULE', props.MODULE);
    if (!_.isEqual(props.MODULE, activeModule)) {
      const _module = _.cloneDeep(props.MODULE);
      setActiveModule(_module);
    }
  }, [props?.MODULE?.name]);

  useEffect(() => {
    if (activeModule === null) return;
    const _widgets = getFilteredWidgets(activeModule);
    if (!_.isEqual(_widgets, widgets)) {
      if (
        ENV.VITE_APP_IS_WEB === 'true' &&
        metadata?.EntitiesAsyncStatus &&
        metadata?.EntitiesAsyncStatus === 'PENDING' &&
        user?.isLoggingOut !== true
      ) {
        setShowSplashScreen(true);
      }

      setWidgets(_widgets);

      if (ENV.VITE_APP_IS_WEB === 'true' && user?.isLoggingOut !== true) {
        setTimeout(() => setShowSplashScreen(false), 500);
      }
    }
  }, [activeModule, metadata]);

  // useEffect(() => {
  //   if (
  //     ENV?.VITE_APP_DISABLE_SCREEN_SIZE_ALERT === 'true' &&
  //     ENV?.VITE_APP_IS_WEB === 'true'
  //   ) {
  //     return;
  //   }

  //   if (!ENV?.VITE_APP_IS_WEB || ENV?.VITE_APP_IS_WEB === 'false') {
  //     return;
  //   }

  //   if (windowWidth < 911) {
  //     dispatch(
  //       ALERT.SetAlert({
  //         type: 'ALERT_SCREEN_SIZE',
  //       } as any)
  //     );
  //   } else if (
  //     windowWidth > 911 &&
  //     alertStore.alert.type === 'ALERT_SCREEN_SIZE'
  //   ) {
  //     dispatch(ALERT.ResetAlert());
  //   }
  // }, [windowWidth, alertStore.alert.type]);

  // useEffect(() => {
  //   const handleResize = () => setWindowWidth(window.innerWidth);
  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  const getScroll = () => {
    if (ENV?.VITE_APP_IS_WEB === 'true') return true;
    if (
      pathSegment === 'household' ||
      pathSegment === 'community' ||
      (pathSegment === '' && ENV?.VITE_APP_IS_WEB !== 'true')
    )
      return true;
    return false;
  };

  // Get background color from module configuration
  const getModuleBackgroundColor = () => {
    return activeModule?.backgroundColor?.default || null;
  };

  const moduleBackgroundColor = getModuleBackgroundColor();

  return (
    <>
      {ENV.VITE_APP_ALLOW_INSPECTION === 'true' && (
        <Inspector
          {...{
            setShownInspector,
            shownInspector,
            devModeActive,
            setDevModeActive,
          }}
        />
      )}

      {ENV.VITE_APP_IS_WEB === 'true' && showSplashScreen ? (
        <div
          className="flex flex-align-center flex-justify-center flex-column "
          style={{
            height: '100vh',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 9999999999999999999,
            backgroundColor: moduleBackgroundColor || '#f7f7f7',
          }}
        >
          <IconWrapper
            iconName={ENV?.VITE_APP_IS_WEB === 'true' ? 'logo_wide' : 'logo'}
          />
        </div>
      ) : null}

      {activeModule && (
        <>
          {Resolvers?.map((Resolver: any) => (
            <Resolver key={Resolver} />
          ))}
          {MetaResolvers?.map((MetaResolver: any) => (
            <MetaResolver key={MetaResolver} />
          ))}
        </>
      )}

      {activeModule && widgets ? (
        <IonContent 
          scrollY={getScroll()} 
          ref={scrollRef}
          style={moduleBackgroundColor ? { 
            '--background': moduleBackgroundColor,
            backgroundColor: moduleBackgroundColor 
          } : {}}
        >
          {/* Add safe area padding container */}
          <div
            className={` ${
              ENV?.VITE_APP_IS_WEB === 'true'
                ? 'WidgetsContainer'
                : 'WidgetsContainer__Mobile'
            }`}
            ref={equalizerRef}
            style={{
              ...(activeModule.needsAuth && ENV?.VITE_APP_IS_WEB === 'false' && !window.location.hostname.includes('kwick') && !window.location.pathname.includes('kwick')
                ? {
                    paddingTop: 'env(safe-area-inset-top, -10px)',
                    boxSizing: 'border-box',
                  }
                : {}),
              ...(moduleBackgroundColor ? { backgroundColor: moduleBackgroundColor } : {})
            }}
          >
            {widgets.length > 0 &&
              widgets.map((widget: any, widget_i: number) => {
                return Factory({
                  ...widget,
                  layouts: layouts,
                  setActiveModule,
                  showInspector,
                  normalizeRender: true,
                  isAndroid,
                  isIPhone16OrDynamicIslandDevice,
                } as any);
              })}
          </div>
          {alertStore.alert.visible && <Alert />}
        </IonContent>
      ) : null}
    </>
  );
};

export default React.memo(Modules);
